import { useState, type MouseEvent } from 'react';

import CheckRoundedIcon from '@mui/icons-material/CheckRounded';
import TranslateRoundedIcon from '@mui/icons-material/TranslateRounded';
import IconButton from '@mui/material/IconButton';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import Tooltip from '@mui/material/Tooltip';
import { useTranslation } from 'react-i18next';

import { LANGUAGES } from '@/i18n';

/**
 * Language switcher (spec §8.9).
 *
 * Each language is written in its own language — "Română", not "Romanian" —
 * because the person looking for it does not necessarily read the current one.
 * The choice is persisted by the detector, so it survives a reload and outranks
 * the browser's own setting from then on.
 */
export function LanguageToggle() {
  const { t, i18n } = useTranslation();
  const [anchor, setAnchor] = useState<HTMLElement | null>(null);

  const current = i18n.resolvedLanguage ?? i18n.language;

  const choose = (code: string): void => {
    setAnchor(null);
    void i18n.changeLanguage(code);
  };

  return (
    <>
      <Tooltip title={t('language.change')}>
        <IconButton
          size="small"
          onClick={(event: MouseEvent<HTMLElement>) => setAnchor(event.currentTarget)}
          aria-label={t('language.change')}
          aria-haspopup="menu"
          aria-expanded={anchor !== null}
        >
          <TranslateRoundedIcon fontSize="small" />
        </IconButton>
      </Tooltip>

      <Menu
        anchorEl={anchor}
        open={anchor !== null}
        onClose={() => setAnchor(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        {LANGUAGES.map((language) => (
          <MenuItem
            key={language.code}
            selected={language.code === current}
            onClick={() => choose(language.code)}
            // The tick is the state; `selected` alone is a background colour.
            aria-current={language.code === current ? 'true' : undefined}
          >
            <ListItemIcon>
              {language.code === current ? (
                <CheckRoundedIcon fontSize="small" />
              ) : (
                <span aria-hidden>{language.flag}</span>
              )}
            </ListItemIcon>
            <ListItemText>{language.label}</ListItemText>
          </MenuItem>
        ))}
      </Menu>
    </>
  );
}
